import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DecimalPipe, Location } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';

import {
  StyleGuideModalDemoComponent,
  type ModalDemoData,
  type ModalDemoRefusal,
} from './style-guide-modal-demo';
import { AiMarkComponent } from '../../shared/ai-mark/ai-mark';
import { IconComponent, type IconName } from '../../shared/icon/icon';
import { StatusChipComponent } from '../../shared/status-chip/status-chip';
import { CollapsiblePanelComponent } from '../../shared/collapsible-panel/collapsible-panel';
import { StageRowComponent } from '../../shared/stage-row/stage-row';
import { TermComponent } from '../../shared/term/term';
import { FirstLookComponent } from '../../shared/first-look/first-look';
import { PRE_REVIEW_STATUS_META } from '../../core/applications/pre-application.types';
import {
  KS_STAGE_META,
  KS_STATUS_META,
  type KsApplicationStage,
  type KsFirstLook,
  type KsStageRow,
} from '../../core/kickstart/kickstart.types';
import {
  DF_APPROVAL_STATUS_META,
  DF_INVOICE_STATUS_META,
  DF_STAGE_META,
  DF_STATUS_META,
} from '../../core/df/df.types';

/** A demo row for the table section — shaped like a real applications worklist row. */
interface DemoRow {
  shortId: string;
  applicant: string;
  product: 'KS' | 'DF';
  amount: number;
  /** Any platform status string — `<app-status-chip>` resolves the label. The
   *  two products share one application-stage vocabulary, so the demo mixes
   *  KS and DF rows without needing two types. */
  status: string;
  submitted: string;
}

/** Who a page is built for. Each has its own shell, and not every pattern
 *  appears on every persona's pages — the selector filters the guide to one. */
type Persona = 'business' | 'agent' | 'ops' | 'admin';
type PersonaFilter = 'all' | Persona;

const PERSONAS: readonly { value: Persona; label: string }[] = [
  { value: 'business', label: 'Business' },
  { value: 'agent', label: 'Agent' },
  { value: 'ops', label: 'Ops' },
  { value: 'admin', label: 'Admin' },
];

/**
 * The sections that belong to some personas only. A section not listed here is
 * shared by every persona (tokens, buttons, tables, feedback…).
 */
const SECTION_PERSONAS: Partial<Record<string, readonly Persona[]>> = {
  'dashboard-cards': ['agent', 'ops', 'admin'],
  'app-entry': ['business', 'agent'],
  terms: ['ops'],
  stepper: ['business', 'agent'],
  'stage-row': ['business', 'agent', 'ops'],
  offer: ['business', 'agent'],
  upload: ['business', 'agent', 'ops'],
  'role-gating': ['ops', 'admin'],
  outstanding: ['ops'],
  'context-blocks': ['business', 'agent', 'ops'],
  'first-look': ['ops'],
};

@Component({
  selector: 'app-style-guide',
  imports: [
    DecimalPipe,
    ReactiveFormsModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDividerModule,
    MatFormFieldModule,
    MatInputModule,
    MatMenuModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSnackBarModule,
    MatTableModule,
    MatTabsModule,
    MatTooltipModule,
    AiMarkComponent,
    IconComponent,
    StatusChipComponent,
    CollapsiblePanelComponent,
    StageRowComponent,
    TermComponent,
    FirstLookComponent,
  ],
  templateUrl: './style-guide.html',
  styleUrl: './style-guide.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StyleGuideComponent implements AfterViewInit {
  private readonly fb = inject(FormBuilder);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);
  private readonly location = inject(Location);
  private readonly router = inject(Router);

  // ─── Persona filter (?persona=ops) ─────────────────────────────────────────
  protected readonly personas = PERSONAS;
  protected readonly persona = signal<PersonaFilter>(
    readPersona(inject(ActivatedRoute).snapshot.queryParamMap.get('persona')),
  );

  protected readonly personaLabel = computed(
    () => PERSONAS.find((p) => p.value === this.persona())?.label ?? 'Everyone',
  );

  /** Whether a section applies to the selected persona. */
  protected shows(sectionId: string): boolean {
    const persona = this.persona();
    const only = SECTION_PERSONAS[sectionId];
    return persona === 'all' || !only || only.includes(persona);
  }

  /** The in-page nav, minus the sections the selected persona never sees. */
  protected readonly visibleNavGroups = computed(() =>
    this.navGroups
      .map((group) => ({ ...group, links: group.links.filter((link) => this.shows(link.id)) }))
      .filter((group) => group.links.length > 0),
  );

  /** Keeps the choice in the URL so a persona's guide is linkable. */
  protected setPersona(value: PersonaFilter): void {
    this.persona.set(value);
    void this.router.navigate([], {
      queryParams: { persona: value === 'all' ? null : value },
      queryParamsHandling: 'merge',
      preserveFragment: true,
      replaceUrl: true,
    });
  }

  // ─── Page navigation (sticky in-page TOC) ──────────────────────────────────
  /** FLOW-224's first look, with a figure shaped like a real run's. */
  protected readonly demoFirstLook: KsFirstLook = {
    amount: '454665.60',
    bank_statement_months: 6,
    financial_statements: true,
    as_at: '2026-10-20T09:15:00Z',
  };

  protected readonly navGroups = [
    {
      label: 'Overview',
      links: [
        { id: 'page-layout', label: 'Page layout' },
        { id: 'dashboard-cards', label: 'Dashboard cards' },
        { id: 'app-entry', label: 'Application entry' },
      ],
    },
    {
      label: 'Data display',
      links: [
        { id: 'tables', label: 'Tables' },
        { id: 'status-chips', label: 'Status chips' },
        { id: 'search', label: 'Search & filters' },
        { id: 'pagination', label: 'Pagination' },
        { id: 'states', label: 'Empty & loading' },
        { id: 'icons', label: 'Iconography' },
      ],
    },
    {
      label: 'Components',
      links: [
        { id: 'buttons', label: 'Buttons' },
        { id: 'forms', label: 'Form fields' },
        { id: 'cards', label: 'Cards' },
        { id: 'badges', label: 'Badges & pills' },
        { id: 'collapsible', label: 'Collapsible panel' },
        { id: 'terms', label: 'Glossary terms' },
      ],
    },
    {
      label: 'Feedback',
      links: [
        { id: 'toasts', label: 'Toasts' },
        { id: 'dialogs', label: 'Confirm dialogs' },
        { id: 'modals', label: 'Form modals' },
        { id: 'errors', label: 'Error banners' },
        { id: 'in-form-refusal', label: 'In-form refusal' },
        { id: 'report', label: 'Report widget' },
      ],
    },
    {
      label: 'Navigation',
      links: [
        { id: 'breadcrumbs', label: 'Breadcrumbs' },
        { id: 'tabs', label: 'Tabs' },
        { id: 'stepper', label: 'Stepper' },
        { id: 'stage-row', label: 'Stage row' },
        { id: 'menus', label: 'Menus' },
      ],
    },
    {
      label: 'Detail patterns',
      links: [
        { id: 'facts', label: 'Fact lists' },
        { id: 'timeline', label: 'Status timeline' },
        { id: 'offer', label: 'Offer panel' },
        { id: 'upload', label: 'Document upload' },
        { id: 'role-gating', label: 'Role-gated UI' },
        { id: 'outstanding', label: 'Outstanding by stage' },
        { id: 'context-blocks', label: 'Context blocks' },
        { id: 'first-look', label: 'First look' },
      ],
    },
    {
      label: 'Utilities',
      links: [
        { id: 'tooltips', label: 'Tooltips' },
        { id: 'dividers', label: 'Dividers' },
        { id: 'progress', label: 'Progress' },
        { id: 'prose-lists', label: 'Prose lists' },
      ],
    },
    {
      label: 'Foundations',
      links: [
        { id: 'colour', label: 'Colour' },
        { id: 'typography', label: 'Typography' },
        { id: 'spacing', label: 'Spacing' },
        { id: 'radius', label: 'Radius' },
        { id: 'shadows', label: 'Shadows' },
        { id: 'motion', label: 'Motion' },
        { id: 'states-matrix', label: 'Interaction states' },
        { id: 'appendix', label: 'Microcopy & a11y' },
      ],
    },
  ] as const;

  /**
   * Smooth-scrolls to an in-page section and reflects it as `#id` in the URL so
   * the section is shareable / quotable. We handle the click ourselves and
   * preventDefault so the Angular router never sees the `#fragment` href and
   * tries to route away from /style-guide; `Location.replaceState` updates the
   * address bar without a navigation or history entry per click.
   */
  protected scrollToSection(event: Event, id: string): void {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.location.replaceState(`${this.pathWithoutFragment()}#${id}`);
  }

  /** The current path + query string, with any existing `#fragment` stripped. */
  private pathWithoutFragment(): string {
    const path = this.location.path(true);
    const hashIndex = path.indexOf('#');
    return hashIndex >= 0 ? path.slice(0, hashIndex) : path;
  }

  /**
   * On load (e.g. a refresh on a deep-linked `…/style-guide#menu` URL), jump to
   * the section named by the URL fragment. The view must be rendered first, so
   * this runs in AfterViewInit.
   */
  ngAfterViewInit(): void {
    const id = this.readFragment();
    if (!id) return;
    // Defer one tick so layout (sticky header, async-rendered sections) has
    // settled before we measure the target's position. No smooth scroll on
    // initial load — land directly on the target.
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ block: 'start' }));
  }

  /** Reads the current URL fragment (the part after `#`), if any. */
  private readFragment(): string {
    const hashIndex = this.location.path(true).indexOf('#');
    return hashIndex >= 0 ? this.location.path(true).slice(hashIndex + 1) : '';
  }

  // ─── Forms ──────────────────────────────────────────────────────────────────
  protected readonly demoForm = this.fb.group({
    text: [''],
    select: [''],
    checkbox: [false],
  });

  protected readonly errorForm = this.fb.group({
    errorField: ['', Validators.required],
  });

  // ─── Tables ───────────────────────────────────────────────────────────────
  protected readonly tableColumns = [
    'shortId',
    'applicant',
    'product',
    'amount',
    'status',
    'submitted',
    'actions',
  ];

  protected readonly demoRows: readonly DemoRow[] = [
    {
      shortId: 'APP-1042',
      applicant: 'Thabo Trading CC',
      product: 'KS',
      amount: 250000,
      status: 'SUBMITTED',
      submitted: '12 Jun 2026',
    },
    {
      shortId: 'APP-1041',
      applicant: 'Nomvula Logistics',
      product: 'DF',
      amount: 1200000,
      status: 'MORE_INFO_REQUIRED',
      submitted: '11 Jun 2026',
    },
    {
      shortId: 'APP-1038',
      applicant: 'Cape Fynbos Foods',
      product: 'KS',
      amount: 80000,
      status: 'ACTIVE',
      submitted: '09 Jun 2026',
    },
    {
      shortId: 'APP-1035',
      applicant: 'Karoo Engineering',
      product: 'DF',
      amount: 540000,
      status: 'IN_PROGRESS',
      submitted: '07 Jun 2026',
    },
    {
      shortId: 'APP-1031',
      applicant: 'Sibanye Retail',
      product: 'KS',
      amount: 150000,
      status: 'DECLINED',
      submitted: '04 Jun 2026',
    },
  ];

  /** Density toggle for the table demo. */
  protected readonly tableDensity = signal<'compact' | 'default' | 'comfortable'>('default');

  // ─── Search & filters ───────────────────────────────────────────────────────
  protected readonly searchTerm = signal('');
  protected readonly activeFilters = signal<string[]>(['Product: KS', 'Status: Under review']);

  protected removeFilter(filter: string): void {
    this.activeFilters.update((f) => f.filter((x) => x !== filter));
  }

  protected clearFilters(): void {
    this.activeFilters.set([]);
  }

  protected clearSearch(): void {
    this.searchTerm.set('');
  }

  // ─── Pagination ─────────────────────────────────────────────────────────────
  protected readonly pageSize = signal(25);
  protected readonly totalItems = 143;

  protected readonly pageRangeLabel = computed(
    () =>
      `Showing 1–${Math.min(this.pageSize(), this.totalItems)} of ${this.totalItems} applications`,
  );

  // ─── Iconography ──────────────────────────────────────────────────────────
  // A Record, so adding an icon to IconName without saying what it means won't compile.
  private readonly iconPurposes: Record<IconName, string> = {
    building: 'A business',
    document: 'A single document',
    'file-text': 'An application or enquiry',
    chart: 'Results / analytics',
    'arrow-right': 'Forward navigation, CTAs',
    'arrow-left': 'Back navigation',
    sparkles: 'A guided start; AI as a system topic. Never "the AI wrote this"',
    bot: 'AI-generated content — use through app-ai-mark',
    briefcase: "Industry / sector; an agent's portfolio",
    calendar: 'Dates, trading since',
    users: 'A team, agents, people',
    user: 'One person, the profile',
    shield: 'System / platform context',
    tag: 'Entity type / legal form',
    trash: 'Delete (destructive), deleted records',
    restore: 'Restore a soft-deleted record — not "Try again"',
    refresh: 'Try again, reload',
    plus: 'Add / create',
    upload: 'Attach a document',
    download: 'Export / download',
    check: 'Accept / confirm / success',
    'check-circle': 'Finished or passed',
    search: 'Search inputs',
    filter: 'Filter controls',
    x: 'Clear / dismiss',
    'x-circle': 'Error feedback / toast',
    info: 'Informational feedback',
    'alert-triangle': 'Warning state',
    'alert-octagon': 'Major flag: stops a figure or a decision',
    'chevron-up': 'Sort ascending, collapse',
    'chevron-down': 'Sort descending, expand',
    'chevron-left': 'Previous (pagination)',
    'chevron-right': 'Next, breadcrumb separator',
    'chevrons-up-down': 'Unsorted column affordance',
    pencil: 'Edit row action',
    eye: 'View row action; show password',
    clock: 'Pending / expiry / timeline',
    'more-vertical': 'Row action menu',
    menu: 'Open a menu of page actions',
    wallet: 'An amount of money',
    zap: 'Kickstart Cash',
    'trending-up': 'Debtors Finance',
    scale: 'A decision or Credit Committee matter',
    book: 'Reference, the glossary',
    database: 'Raw records (Admin)',
    inbox: 'A worklist or queue',
    lock: 'Security, something locked',
    mail: 'Email',
    phone: 'Phone number',
    smartphone: 'Authenticator / two-factor',
    copy: 'Copy to clipboard',
    camera: 'Take a photo',
    image: 'An image file',
    lightbulb: 'A tip or suggestion',
    'message-square': 'A message or note',
    grip: 'Drag to reorder',
    bug: 'Debug tools, reporting a problem',
    sun: 'Light mode',
    moon: 'Dark mode',
    'log-in': 'Log in',
    'log-out': 'Log out',
  };

  protected readonly iconCatalogue = (Object.keys(this.iconPurposes) as IconName[]).map((name) => ({
    name,
    purpose: this.iconPurposes[name],
  }));

  // One concept, one icon. A page's title icon comes from this list.
  protected readonly iconConcepts: readonly { concept: string; name: IconName }[] = [
    { concept: 'Kickstart Cash', name: 'zap' },
    { concept: 'Debtors Finance', name: 'trending-up' },
    { concept: 'A business', name: 'building' },
    { concept: 'An application or enquiry', name: 'file-text' },
    { concept: 'A team', name: 'users' },
    { concept: 'Money', name: 'wallet' },
    { concept: 'Try again', name: 'refresh' },
    { concept: 'AI wrote this', name: 'bot' },
  ];

  protected readonly iconSizes = [16, 18, 20, 24, 32, 40] as const;

  // ─── Status chips ───────────────────────────────────────────────────────────
  // Grouped by the axis they belong to, because the platform has no single
  // status machine: a Kickstart facility, its application stage, and a DF
  // invoice are three unrelated vocabularies that happen to share a chip.
  // Ungrouped, the gallery reads as one enum and invites picking a label off
  // the wrong axis.
  protected readonly statusGroups: readonly { label: string; statuses: string[] }[] = [
    { label: 'Kickstart Cash — facility', statuses: Object.keys(KS_STATUS_META) },
    { label: 'Kickstart Cash — application stage', statuses: Object.keys(KS_STAGE_META) },
    { label: 'Debtors Finance — facility', statuses: Object.keys(DF_STATUS_META) },
    { label: 'Debtors Finance — application stage', statuses: Object.keys(DF_STAGE_META) },
    { label: 'Debtors Finance — invoice', statuses: Object.keys(DF_INVOICE_STATUS_META) },
    { label: 'Debtors Finance — debtor approval', statuses: Object.keys(DF_APPROVAL_STATUS_META) },
    { label: 'Pre-application review', statuses: Object.keys(PRE_REVIEW_STATUS_META) },
  ];

  // ─── Stepper demo ───────────────────────────────────────────────────────────
  protected readonly steps = [
    { label: 'Details', state: 'complete' as const },
    { label: 'Documents', state: 'active' as const },
    { label: 'Consent', state: 'upcoming' as const },
    { label: 'Review', state: 'upcoming' as const },
  ];

  // ─── Stage row demo (FLOW-402, DOC-89-A §5) — the back end's `stage_row` ───
  protected readonly stageRow: KsStageRow = {
    reached: 'OFFER',
    label: 'Offer',
    steps: [
      { key: 'SUBMITTED', label: 'Submitted', state: 'passed' },
      { key: 'BEING_ASSESSED', label: 'Being assessed', state: 'passed' },
      { key: 'OFFER', label: 'Offer', state: 'reached' },
      { key: 'SIGNED', label: 'Signed', state: 'ahead' },
      { key: 'PAID_OUT', label: 'Paid out', state: 'ahead' },
    ],
  };

  // ─── Timeline demo (mirrors StatusEvent shape) ───────────────────────────────
  protected readonly timeline = [
    {
      status: 'MORE_INFO_REQUIRED' as KsApplicationStage,
      actor: 'Analyst — Jane Dube',
      note: 'Routed to credit for assessment.',
      date: '12 Jun 2026, 14:32',
    },
    {
      status: 'SUBMITTED' as KsApplicationStage,
      actor: 'CapX system',
      note: '',
      date: '11 Jun 2026, 09:05',
    },
    {
      status: 'DRAFT' as KsApplicationStage,
      actor: 'Thabo Mokoena',
      note: 'Application started.',
      date: '10 Jun 2026, 16:48',
    },
  ];

  // ─── Toasts ─────────────────────────────────────────────────────────────────
  protected showToast(kind: 'success' | 'error' | 'info' | 'saved'): void {
    const messages = {
      success: 'Business restored.',
      error: 'Could not restore business. Try again.',
      info: 'Your application is under review.',
      saved: 'Details updated.',
    } as const;
    this.snackBar.open(messages[kind], 'Dismiss', {
      duration: kind === 'error' ? 6000 : 4000,
      horizontalPosition: 'right',
      verticalPosition: 'bottom',
      panelClass: ['sg-snack', `sg-snack--${kind === 'saved' ? 'success' : kind}`],
    });
  }

  // ─── Form modals ────────────────────────────────────────────────────────────
  protected openModalDemo(refusal: ModalDemoRefusal = null): void {
    this.dialog
      .open<StyleGuideModalDemoComponent, ModalDemoData, boolean>(StyleGuideModalDemoComponent, {
        data: { refusal },
      })
      .afterClosed()
      .subscribe((saved) => {
        if (saved) this.showToast('saved');
      });
  }

  protected readonly modalFieldRefusal: ModalDemoRefusal = {
    mobile: ['That number is already on another account.'],
  };
  protected readonly modalActRefusal: ModalDemoRefusal =
    'Your details changed in another window. Reload the page and try again.';

  protected readonly modalUsage = `<app-modal
  heading="Update your details"
  description="We use these to reach you about your applications."
  submitLabel="Save changes"
  [formGroup]="form"
  [pending]="pending()"
  [error]="error()"
  (submitted)="save()"
>
  <mat-form-field appearance="outline">…</mat-form-field>
</app-modal>`;

  // ─── Loading / skeleton ─────────────────────────────────────────────────────
  protected readonly skeletonRows = [0, 1, 2, 3, 4];

  // ─── Foundations ────────────────────────────────────────────────────────────
  protected readonly colorTokens = [
    { name: '--background', label: 'Background' },
    { name: '--foreground', label: 'Foreground' },
    { name: '--card', label: 'Card' },
    { name: '--primary', label: 'Primary' },
    { name: '--primary-foreground', label: 'Primary Foreground' },
    { name: '--secondary', label: 'Secondary' },
    { name: '--muted', label: 'Muted' },
    { name: '--muted-foreground', label: 'Muted Foreground' },
    { name: '--accent', label: 'Accent' },
    { name: '--border', label: 'Border' },
    { name: '--ring', label: 'Ring' },
    { name: '--page-bg', label: 'Page BG' },
    { name: '--brand-red', label: 'Brand Red' },
    { name: '--brand-navy', label: 'Brand Navy' },
    { name: '--color-status-primary', label: 'Status Primary' },
    { name: '--color-status-primary-light', label: 'Status Primary Light' },
    { name: '--color-body-text', label: 'Body Text' },
    { name: '--color-gray-500', label: 'Gray 500' },
    { name: '--color-gray-600', label: 'Gray 600' },
  ] as const;

  protected readonly spacingTokens = [
    { name: '--space-1', px: '4px' },
    { name: '--space-2', px: '8px' },
    { name: '--space-3', px: '12px' },
    { name: '--space-4', px: '16px' },
    { name: '--space-5', px: '20px' },
    { name: '--space-6', px: '24px' },
    { name: '--space-8', px: '32px' },
    { name: '--space-10', px: '40px' },
    { name: '--space-12', px: '48px' },
    { name: '--space-16', px: '64px' },
  ] as const;

  protected readonly radiusTokens = [
    { name: '--radius-sm', label: 'radius-sm', note: '≈ 6px — badges/chips' },
    { name: '--radius-md', label: 'radius-md', note: '≈ 8px — buttons/inputs' },
    { name: '--radius-lg', label: 'radius-lg', note: '≈ 10px — cards/panels' },
    { name: '--radius-xl', label: 'radius-xl', note: '≈ 14px — large cards/modals' },
    { name: '--radius-pill', label: 'radius-pill', note: 'fully rounded — chips, avatars, dots' },
  ] as const;

  protected readonly shadowTokens = [
    { name: '--shadow-1', note: 'Cards, outlined panels' },
    { name: '--shadow-2', note: 'Dropdowns, popovers' },
    { name: '--shadow-3', note: 'Hover-lifted cards' },
    { name: '--shadow-4', note: 'Modals, overlays' },
  ] as const;

  // ─── Dashboard cards (KPI tiles) ──────────────────────────────────────────
  /** Mirrors the agent dashboard KPI row; rendered with the shared `.dash-kpi` tile. */
  protected readonly dashboardCards: readonly {
    icon: IconName;
    tone: '' | 'success' | 'warning';
    value: string;
    label: string;
  }[] = [
    { icon: 'building', tone: '', value: '1', label: 'Linked clients' },
    { icon: 'document', tone: '', value: '0', label: 'Live applications' },
    { icon: 'check', tone: 'success', value: '0', label: 'All up to date' },
    { icon: 'wallet', tone: 'success', value: 'ZAR 0', label: 'Funding facilitated' },
  ];

  constructor() {
    this.errorForm.controls.errorField.markAsTouched();
  }
}

function readPersona(value: string | null): PersonaFilter {
  return PERSONAS.some((p) => p.value === value) ? (value as Persona) : 'all';
}
